import {
  Box,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { IContactPhoneNumberData } from "../ContactPhoneNumberData";

interface IUpdatePhoneProps {
  phoneNumbers: IContactPhoneNumberData;
  onChange: (field: keyof IContactPhoneNumberData, newValue: string) => void;
  onErrorChange: (hasError: boolean) => void;
}

export const UpdatePhone: React.FC<IUpdatePhoneProps> = ({
  phoneNumbers,
  onChange,
  onErrorChange,
}) => {
  const formatPhone = (input: string) => {
    const cleaned = input.replace(/\D+/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6,10)}`;
  };

  const handlePhoneInputChange = (
    field: keyof IContactPhoneNumberData,
    input: string
  ) => {
     onChange(field, input.replace(/\D+/g, "").slice(0, 10));
  };

  const isValidPhoneField = (phone: string) =>
    phone.length === 0 || phone.length === 10;

  const primaryError = !isValidPhoneField(phoneNumbers.employeePrimaryPhone);
  const mobileError = !isValidPhoneField(phoneNumbers.employeeMobilePhone);

  useEffect(() => {
    onErrorChange(primaryError || mobileError);
  }, [primaryError, mobileError, onErrorChange]);

  useEffect(() => {
    if (!phoneNumbers.employeePrimaryPhone) {
      onChange("employeePrimaryPhoneExtension", "");
    }
  }, [phoneNumbers.employeePrimaryPhone, onChange]);

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      <Grid container spacing={2} sx={{ justifyContent: "center" }}>
        <Grid item xs={12} md={8} sx={{ p: 0 }}>
          <Typography sx={{ fontWeight: "bold", mb: 1.5 }} data-automation="primary-phone-lbl">
            Primary Phone Number
          </Typography>
          <TextField
            fullWidth
            placeholder={"(___) ___-____"}
            value={formatPhone(phoneNumbers.employeePrimaryPhone || "")}
            onChange={(e) =>
              handlePhoneInputChange("employeePrimaryPhone", e.target.value)
            }
            data-automation="primary-phone-field"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 14,
            }}
            error={primaryError}
          />
          <FormHelperText
            component={"div"}
            data-automation="invalid-primary-lbl"
            error={primaryError}
          >
            {primaryError ? "Invalid Phone Number" : ""}
          </FormHelperText>
          <Typography sx={{ fontWeight: "bold", mb: 1.5 }} data-automation="extension-lbl">
            Extension
          </Typography>
          <TextField
            disabled={primaryError || !phoneNumbers.employeePrimaryPhone}
            fullWidth
            value={phoneNumbers.employeePrimaryPhoneExtension || ""}
            onChange={(e) =>
              onChange("employeePrimaryPhoneExtension", e.target.value)
            }
            data-automation="extension-field"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 5,
            }}
          />
          <Typography sx={{ fontWeight: "bold", mt: 10, mb: 1.5 }} data-automation="mobile-phone-lbl">
            Mobile Number
          </Typography>
          <TextField
            fullWidth
            placeholder={"(___) ___-____"}
            value={formatPhone(phoneNumbers.employeeMobilePhone || "")}
            onChange={(e) =>
              handlePhoneInputChange("employeeMobilePhone", e.target.value)
            }
            data-automation="mobile-phone-field"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 14,
            }}
            error={mobileError}
          />
          <FormHelperText
            component={"div"}
            data-automation="invalid-mobile-lbl"
            error={mobileError}
          >
            {mobileError ? "Invalid Phone Number" : ""}
          </FormHelperText>
        </Grid>
      </Grid>
    </Box>
  );
};
